/** Extract entityLinks in the specified range, and return the entities */

import { graphHooks } from '@tunasong/graph-lib/react'
import { isCollabEntity } from '@tunasong/models'
import type { TunaElement } from '@tunasong/models'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { useYDoc } from '@tunasong/plugin-lib'
import type { Entity, EntityLink } from '@tunasong/schemas'
import { isEntityLink } from '@tunasong/schemas'
import { useMemo } from 'react'
import { EntityQueries } from '../editor/queries/entities.js'
import { RangeQueries } from '../editor/queries/range.js'

export const useFromEntityLinks = (editor: TunaEditor | null, at = editor ? RangeQueries.all(editor) : undefined) => {
  /** Extract the links, get the linked entities by ID */
  const entityLinks: TunaElement<EntityLink>[] = editor
    ? (EntityQueries.find(editor, isEntityLink, at).map(e => e[0]) as never)
    : []

  const { entities } = graphHooks.useEntitiesById(entityLinks.map(e => e.ref.id).filter(Boolean) as string[])
  return entities
}

/**
 * Recursively collect `entityLink` nodes from a decoded Slate value — the same
 * result as `EntityQueries.find(editor, isEntityLink)` (an `Editor.nodes`
 * traversal) but WITHOUT a Plate editor. Pure; unit-testable.
 */
export const findEntityLinksInValue = (nodes?: readonly unknown[]): EntityLink[] => {
  const links: EntityLink[] = []
  const walk = (ns?: readonly unknown[]) => {
    if (!Array.isArray(ns)) {
      return
    }
    for (const node of ns) {
      if (isEntityLink(node)) {
        links.push(node)
      }
      const children = (node as { children?: readonly unknown[] } | null)?.children
      if (Array.isArray(children)) {
        walk(children)
      }
    }
  }
  walk(nodes)
  return links
}

/**
 * Resolve a page's entity links WITHOUT materializing a Plate editor.
 *
 * The editor-based path (`usePageTools` → `useCreateEditor` → `useFromEntityLinks`)
 * builds a FULL editor with all plugins plus the decoded doc and holds it for the
 * whole session, purely to read links — a real per-session leak on the
 * performance-mode path (GH #1654 / memory-analysis S3). This decodes the YDoc
 * value (no editor) and walks it directly.
 */
export const usePageLinks = (entity: Entity | null = null) => {
  const { element: decodedPage = entity } = useYDoc(isCollabEntity(entity) ? (entity ?? undefined) : undefined)
  const links = useMemo(
    () => findEntityLinksInValue((decodedPage as { children?: readonly unknown[] } | null)?.children),
    [decodedPage]
  )
  const ids = useMemo(() => links.map(link => link.ref.id).filter(Boolean) as string[], [links])
  const { entities } = graphHooks.useEntitiesById(ids)
  return { links, ids, entities }
}
